import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'

import { useReferral } from '@pages/Referral/Referral'

import { useSessionEntity } from '@context/EntityContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSignup } from '@context/SignupContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum, isTickmillSCType } from '@utils/companyName.utils'

import { NameDto } from '@model/NameDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { useFetchRegulators } from '@hooks/useFetchRegulators'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { SignupWarningMessages } from '../../../Components/SignupWarningMessages'
import { useLeadProducts } from '../../../hooks/useProducts'
import { PersonalDetailsTemplate } from '../PersonalDetailsTemplate'
import {
  PersonalDetailsStep4Form,
  PersonalDetailsStep4FormValues,
} from './PersonalDetailsStep4Form'

interface PersonalDetailsStep4PageProps {
  onSubmit(values: PersonalDetailsStep4FormValues): void
}

export const PersonalDetailsStep4Page: React.FC<PersonalDetailsStep4PageProps> = (props) => {
  const { onSubmit } = props

  const { signupData } = useSignup()
  const [taxUnavailableReasons, setTaxUnavailableReasons] = useState<NameDto[]>([])
  const apiClient = useApiClient(ClientApiClient)

  useLeadProducts(signupData.lead)

  const locale = useSessionLanguage()
  const entity = useSessionEntity()
  const referral = useReferral()
  const regulators = useFetchRegulators()

  const isStocksOnlyClient = useMemo(() => {
    const products = signupData.lead?.products || []
    return (
      products.length > 0 &&
      products.every((p) => p.id === TickmillProductType.STOCKS) &&
      products.length === 1
    )
  }, [signupData.lead?.products])

  const availableProducts = useMemo(() => {
    if (!isStocksOnlyClient || !signupData.lead || !regulators.data) {
      return []
    }

    const isTickmillUK = TickmillCompaniesEnum.TICKMILL_UK
    const isLeadFromTickmillUK = signupData.lead?.tickmillCompany?.id === isTickmillUK
    const offeredTickmillUkProductIds = [TickmillProductType.CFD, TickmillProductType.ETD]

    if (!isLeadFromTickmillUK) {
      return []
    }

    const regulatorOption = regulators.data?.find(
      (r) => r.country.id === signupData.lead?.country?.id
    )
    const regulator = regulatorOption?.regulators.find((r) => r.tickmillCompanyId === isTickmillUK)

    return (
      regulator?.products?.filter(
        (p) => offeredTickmillUkProductIds.includes(p.id) && p.id !== TickmillProductType.STOCKS
      ) || []
    )
  }, [isStocksOnlyClient, signupData.lead, regulators.data])

  const getTaxUnavailableReasons = async () => {
    const resp = await apiClient.getTaxUnavailableReasons(locale)
    setTaxUnavailableReasons(resp)
  }

  useEffect(() => {
    getTaxUnavailableReasons()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const { data: nationalities = [], isLoading: nationalitiesLoading } = useQuery({
    queryKey: queryKeys.signup.nationalities(),
    queryFn: () => apiClient.getNationalities(),
  })
  const isPageLoading = nationalitiesLoading || regulators.isLoading
  const { referral_main_ib_code, aff_token } = referral.getReferralTokenValues(signupData.lead)

  return (
    <Loading isLoading={isPageLoading} showLoadingIcon>
      <PersonalDetailsTemplate lead={signupData.lead} step={4} reversed>
        <Box flex direction='column' gap='x16'>
          <SignupWarningMessages leadTypeId={signupData.lead?.type?.id} />

          <PersonalDetailsStep4Form
            lead={signupData.lead}
            entity={entity}
            initialValues={
              {
                ...signupData.values,
                mainIbCode: referral_main_ib_code,
                affiliateToken: aff_token,
              } as PersonalDetailsStep4FormValues
            }
            nationalities={nationalities}
            country={signupData.lead?.country}
            taxReasons={taxUnavailableReasons}
            onSubmit={onSubmit}
            requireTaxId={!isTickmillSCType(entity)}
            isStocksOnlyClient={isStocksOnlyClient}
            regulatorOptions={regulators.data}
            availableProducts={availableProducts}
            productsLoading={regulators.isLoading}
          />
        </Box>
      </PersonalDetailsTemplate>
    </Loading>
  )
}
